/**
 * Data layer for `custom-tracking-domain-via-cname`. Thin typed fetch wrappers over /api/v1/custom-tracking-domain-via-cname.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CustomTrackingDomainViaCnameRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCustomTrackingDomainViaCnameResponse {
	count: number;
	items: CustomTrackingDomainViaCnameRow[];
}

export interface CreateCustomTrackingDomainViaCnameBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/custom-tracking-domain-via-cname";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCustomTrackingDomainViaCnameResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCustomTrackingDomainViaCnameResponse>(res);
}

export async function create(body: CreateCustomTrackingDomainViaCnameBody): Promise<CustomTrackingDomainViaCnameRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CustomTrackingDomainViaCnameRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const customTrackingDomainViaCnameQueryKeys = {
	all: ["custom-tracking-domain-via-cname"] as const,
	list: () => [...customTrackingDomainViaCnameQueryKeys.all] as const,
	detail: (id: string) => [...customTrackingDomainViaCnameQueryKeys.all, id] as const,
};
